// Fonte unica dei link di navigazione del sito istituzionale (header + footer).
// Le voci sono costruite dai registry di features e solutions: aggiungendo una
// pagina al registry, menu e footer si aggiornano da soli.
import { FEATURES } from '@/content/features';
import { SOLUTIONS } from '@/content/solutions';

export interface NavLink {
  label: string;
  href: string;
  description?: string;
}

export interface NavGroup {
  label: string;
  items: NavLink[];
  indexLink: NavLink;
}

export const featuresGroup: NavGroup = {
  label: 'Funzionalità',
  items: FEATURES.map(f => ({
    label: f.navLabel,
    href: `/funzionalita/${f.slug}/`,
    description: f.menuDescription,
  })),
  indexLink: { label: 'Tutte le funzionalità', href: '/funzionalita/' },
};

export const solutionsGroup: NavGroup = {
  // In barra di navigazione il taglio è per destinatario, non per categoria
  label: 'Dedicato a',
  items: SOLUTIONS.map(s => ({
    label: s.navLabel,
    href: `/soluzioni/${s.slug}/`,
    description: s.navDescription,
  })),
  indexLink: { label: 'Tutte le soluzioni', href: '/soluzioni/' },
};

/** Slug delle soluzioni rivolte a organizzazioni, nell'ordine della colonna */
const ORGANIZATION_SLUGS = ['studi-legali', 'aziende'];

const isOrganization = (link: NavLink) =>
  ORGANIZATION_SLUGS.some(slug => link.href === `/soluzioni/${slug}/`);

/** Le due colonne del mega-menu «Dedicato a» */
export const solutionsColumns: { title: string; items: NavLink[] }[] = [
  { title: 'Professionisti', items: solutionsGroup.items.filter(item => !isOrganization(item)) },
  {
    title: 'Organizzazioni',
    // Ordinate come ORGANIZATION_SLUGS, non come il registry
    items: ORGANIZATION_SLUGS.map(slug =>
      solutionsGroup.items.find(item => item.href === `/soluzioni/${slug}/`)
    ).filter((item): item is NavLink => item !== undefined),
  },
];

/** Prezzi ha una pagina dedicata: non è più una sezione della homepage */
export const prezziLink: NavLink = { label: 'Prezzi', href: '/prezzi/' };

export const fontiLink: NavLink = { label: 'Fonti', href: '/fonti/' };

/**
 * News: norme, prassi e sentenze appena uscite. Come Strumenti e Confronto sta
 * nel piè di pagina e non in barra: ci si arriva da Google o da un annuncio su
 * una notizia, non è una tappa del percorso di chi sta valutando il prodotto.
 */
export const newsLink: NavLink = { label: 'News', href: '/news/' };

export const strumentiLink: NavLink = { label: 'Strumenti', href: '/strumenti/' };

export const faqLink: NavLink = { label: 'Domande frequenti', href: '/faq/' };

/**
 * Confronto con le AI generaliste. Come Strumenti, vive nel piè di pagina e
 * non in barra: è una porta d'ingresso per chi sta già valutando alternative,
 * non una tappa del percorso di chi scopre il prodotto.
 */
export const confrontoLink: NavLink = {
  // L'etichetta non ripete il titolo della pagina: in una colonna di voci
  // brevi «Confronto con le AI generaliste» dice cosa si trova, il claim lo
  // legge chi arriva.
  label: 'Confronto con le AI generaliste',
  href: '/confronto/',
};

/**
 * Voci semplici della barra di navigazione, dopo i due menu a tendina.
 * Strumenti resta fuori: vive nel footer — è una porta d'ingresso da Google,
 * non una tappa del percorso di chi sta valutando il prodotto.
 */
export const primaryLinks: NavLink[] = [fontiLink, prezziLink];

/** Colonne link del footer istituzionale */
export const footerColumns: { title: string; links: NavLink[] }[] = [
  {
    title: 'Prodotto',
    links: [
      featuresGroup.indexLink,
      ...featuresGroup.items.map(({ label, href }) => ({ label, href })),
      fontiLink,
      newsLink,
      strumentiLink,
      prezziLink,
      confrontoLink,
      faqLink,
    ],
  },
  {
    title: 'Dedicato a',
    links: solutionsGroup.items.map(({ label, href }) => ({ label, href })),
  },
];
